import { useEffect } from "react";
import { useContent } from "@/lib/use-content";
import { articuloPorSlug, articulosPublicados, type Articulo, type ContenidoArticulos } from "@/lib/articulos";
import { DEFAULTS } from "@/lib/content-defaults";
import snapshotJson from "@/lib/content-snapshot.json";

/** Contenido de artículos inicial y síncrono (copia tomada al compilar): lo usa el `head` de las rutas. */
export function contenidoArticulosInicial(): ContenidoArticulos {
  const snap = (snapshotJson as unknown as { articulos?: ContenidoArticulos }).articulos;
  return snap ?? (DEFAULTS.articulos as ContenidoArticulos);
}

/** Artículos publicados según el contenido vivo (CP Admin → Artículos). */
export function useArticulos(): Articulo[] {
  return articulosPublicados(useContent<ContenidoArticulos>("articulos"));
}

export function useArticulo(slug: string): Articulo | undefined {
  return articuloPorSlug(useContent<ContenidoArticulos>("articulos"), slug);
}

export function useHayArticulos(): boolean {
  return useArticulos().length > 0;
}

/** URL inexistente: responde 200 (rewrite de Hosting), así que se marca `noindex` para que Google no la tome por página real. */
export function useNoIndex(activo: boolean) {
  useEffect(() => {
    if (!activo) return;
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex";
    meta.setAttribute("data-cp-404", "1");
    document.head.appendChild(meta);
    return () => { meta.remove(); };
  }, [activo]);
}

/** Título y descripción de la pestaña para el contenido vivo (el `head` de la ruta solo conoce la copia del build). */
export function usePageMeta(titulo: string | undefined, descripcion?: string) {
  useEffect(() => {
    if (!titulo) return;
    const previo = document.title;
    document.title = titulo;
    const desc = document.querySelector('meta[name="description"]');
    const descPrevia = desc?.getAttribute("content") ?? null;
    if (desc && descripcion) desc.setAttribute("content", descripcion);
    return () => { document.title = previo; if (desc && descPrevia !== null) desc.setAttribute("content", descPrevia); };
  }, [titulo, descripcion]);
}
